import { useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Box,
  Button,
  Group,
  NumberInput,
  Paper,
  Portal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconCalculator, IconCornerDownRight, IconX } from '@/components/icons'

function todayDateInputValue(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  const date = new Date(y, m - 1, d, 12, 0, 0, 0)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null
  return date
}

function calculateUsedMonths(paymentDateValue: string, calculationDateValue: string): number {
  const start = parseDateInputValue(paymentDateValue)
  const end = parseDateInputValue(calculationDateValue)
  if (!start || !end || end <= start) return 0
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
  if (end.getDate() > start.getDate()) months += 1
  return Math.max(1, months)
}

function formatRubles(value: number): string {
  const rounded = Math.max(0, Math.round(Number.isFinite(value) ? value : 0))
  return `${rounded.toLocaleString('ru-RU')} ₽`
}

function getRefundCalculatorRate(usedMonths: number, shortTermMonthlyPrice: number): number {
  if (usedMonths > 12) return 290
  if (usedMonths > 6) return 390
  return shortTermMonthlyPrice
}

function getRefundCalculatorInitialPosition(): { left: number; top: number } {
  if (typeof window === 'undefined') return { left: 24, top: 96 }
  return {
    left: Math.max(12, window.innerWidth - 396),
    top: 96,
  }
}

function clampRefundCalculatorPosition(pos: { left: number; top: number }): { left: number; top: number } {
  if (typeof window === 'undefined') return pos
  return {
    left: Math.min(Math.max(8, pos.left), Math.max(8, window.innerWidth - 120)),
    top: Math.min(Math.max(8, pos.top), Math.max(8, window.innerHeight - 80)),
  }
}

export function RefundCalculatorFloating({
  opened,
  operatorId,
  onClose,
  onInsert,
}: {
  opened: boolean
  operatorId?: string | null
  onClose: () => void
  onInsert: (text: string) => void
}) {
  const storageKey = `servicedesk:refund-calculator:${operatorId || 'anonymous'}:position`
  const [paymentDate, setPaymentDate] = useState(todayDateInputValue)
  const [calculationDate, setCalculationDate] = useState(todayDateInputValue)
  const [monthlyPrice, setMonthlyPrice] = useState<string | number>(490)
  const [totalPaid, setTotalPaid] = useState<string | number>('')
  const [position, setPosition] = useState(getRefundCalculatorInitialPosition)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    startLeft: number
    startTop: number
  } | null>(null)

  useEffect(() => {
    if (!opened || typeof window === 'undefined') return
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) {
        setPosition(getRefundCalculatorInitialPosition())
        return
      }
      const parsed = JSON.parse(raw) as { left?: unknown; top?: unknown }
      const left = Number(parsed.left)
      const top = Number(parsed.top)
      if (Number.isFinite(left) && Number.isFinite(top)) {
        setPosition(clampRefundCalculatorPosition({ left, top }))
      }
    } catch {
      setPosition(getRefundCalculatorInitialPosition())
    }
  }, [opened, storageKey])

  useEffect(() => {
    if (!opened || typeof window === 'undefined') return
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return
      event.preventDefault()
      setPosition(
        clampRefundCalculatorPosition({
          left: drag.startLeft + event.clientX - drag.startX,
          top: drag.startTop + event.clientY - drag.startY,
        }),
      )
    }
    const onUp = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || event.pointerId !== drag.pointerId) return
      dragRef.current = null
      setPosition((current) => {
        const clamped = clampRefundCalculatorPosition(current)
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(clamped))
        } catch {
          // ignore local storage errors
        }
        return clamped
      })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [opened, storageKey])

  const monthly = Math.max(0, Number(monthlyPrice) || 0)
  const paid = Math.max(0, Number(totalPaid) || 0)
  const usedMonths = calculateUsedMonths(paymentDate, calculationDate)
  const rate = getRefundCalculatorRate(usedMonths, monthly || 490)
  const usedCost = usedMonths * rate
  const refund = Math.max(0, paid - usedCost)
  const insertText = `к возврату: ${formatRubles(refund)}`

  if (!opened) return null

  return (
    <Portal>
      <Paper
        shadow="xl"
        withBorder
        p="md"
        style={{
          position: 'fixed',
          left: position.left,
          top: position.top,
          width: 'min(360px, calc(100vw - 16px))',
          zIndex: 10000,
        }}
      >
        <Group
          justify="space-between"
          gap="xs"
          mb="sm"
          onPointerDown={(event) => {
            dragRef.current = {
              pointerId: event.pointerId,
              startX: event.clientX,
              startY: event.clientY,
              startLeft: position.left,
              startTop: position.top,
            }
          }}
          style={{ cursor: 'move', userSelect: 'none', touchAction: 'none' }}
        >
          <Group gap={8}>
            <IconCalculator size={18} stroke={1.7} />
            <Text fw={600} size="sm">
              Расчет возврата
            </Text>
          </Group>
          <ActionIcon
            size="sm"
            variant="subtle"
            aria-label="Закрыть калькулятор"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            <IconX size={16} stroke={1.5} />
          </ActionIcon>
        </Group>

        <Stack gap="xs">
          <TextInput
            label="Дата оплаты"
            type="date"
            value={paymentDate}
            onChange={(event) => setPaymentDate(event.currentTarget.value)}
          />
          <TextInput
            label="Дата расчета"
            type="date"
            value={calculationDate}
            onChange={(event) => setCalculationDate(event.currentTarget.value)}
          />
          <Group grow align="flex-start">
            <NumberInput
              label="Стоимость месяца"
              min={0}
              step={10}
              value={monthlyPrice}
              onChange={setMonthlyPrice}
              suffix=" ₽"
            />
            <NumberInput
              label="Оплачено"
              min={0}
              step={100}
              value={totalPaid}
              onChange={setTotalPaid}
              suffix=" ₽"
            />
          </Group>

          <Box
            p="sm"
            style={{
              borderRadius: 6,
              background: 'light-dark(rgba(15, 23, 42, 0.04), rgba(255, 255, 255, 0.06))',
            }}
          >
            <Stack gap={4}>
              <Group justify="space-between" gap={8}>
                <Text size="xs" c="dimmed">
                  Использовано
                </Text>
                <Text size="xs" fw={600}>
                  {usedMonths} мес.
                </Text>
              </Group>
              <Group justify="space-between" gap={8}>
                <Text size="xs" c="dimmed">
                  Ставка
                </Text>
                <Text size="xs" fw={600}>
                  {formatRubles(rate)} / мес.
                </Text>
              </Group>
              <Group justify="space-between" gap={8}>
                <Text size="xs" c="dimmed">
                  Использовано
                </Text>
                <Text size="xs" fw={600}>
                  {usedMonths} × {formatRubles(rate)} = {formatRubles(usedCost)}
                </Text>
              </Group>
              <Group justify="space-between" gap={8} mt={4}>
                <Text size="sm" fw={600}>
                  К возврату
                </Text>
                <Text size="sm" fw={700}>
                  {formatRubles(refund)}
                </Text>
              </Group>
            </Stack>
          </Box>

          <Button
            leftSection={<IconCornerDownRight size={16} stroke={1.5} />}
            onClick={() => onInsert(insertText)}
          >
            Вставить в ответ
          </Button>
        </Stack>
      </Paper>
    </Portal>
  )
}
